import { getSupabase } from '../lib/supabase';

/** Stable concepts, not copy/media revisions. New releases receive new identities. */
export const FEATURE_LESSON_IDS = {
  create: 'creation-basics',
  explore: 'explore-questions',
  calls: 'respondent-calls',
  'auto-run': 'recipe-auto-run',
} as const;

/** Contexts with their own independently released introduction. */
export type FeatureLessonName = keyof typeof FEATURE_LESSON_IDS;
/** An explicit action or actual feature use, distinct from first exposure. */
export type FeatureLessonOutcome = 'completed' | 'dismissed' | 'used';
/** The minimal account receipt returned by the indexed history read. */
interface LearningReceipt { lesson_id: string; outcome: 'seen' | FeatureLessonOutcome }
/** Ephemeral coordination survives route/provider remounts, never browser restarts. */
interface LearningHistory {
  receipts: Map<string, LearningReceipt['outcome']>;
  checked: Set<string>;
  pendingOutcomes: Map<string, Promise<void>>;
}
const histories = new Map<string, Promise<LearningHistory>>();

/** Load just IDs/outcomes once per account per loaded app session, only when needed. */
function historyFor(userId: string): Promise<LearningHistory> {
  const existing = histories.get(userId);
  if (existing) return existing;
  const request = readHistory(userId);
  histories.set(userId, request);
  // A later encounter may retry a failed read. There is no background retry or polling.
  void request.catch(() => { if (histories.get(userId) === request) histories.delete(userId); });
  return request;
}

/** RLS and the composite primary key scope this tiny read to the signed-in account. */
async function readHistory(userId: string): Promise<LearningHistory> {
  const { data, error } = await getSupabase().from('user_feature_learning')
    .select('lesson_id,outcome').eq('user_id', userId);
  if (error) throw new Error('Could not check learning history', { cause: error });
  if (!Array.isArray(data)) throw new Error('Invalid learning history response');
  return {
    receipts: new Map((data as LearningReceipt[]).map(row => [row.lesson_id, row.outcome])),
    checked: new Set(), pendingOutcomes: new Map(),
  };
}

/** Reserve a new introduction atomically; known lessons never cause another request. */
export async function claimFeatureLesson(userId: string, lesson: FeatureLessonName): Promise<boolean> {
  const history = await historyFor(userId);
  const lessonId = FEATURE_LESSON_IDS[lesson];
  if (history.receipts.has(lessonId) || history.checked.has(lessonId)) return false;
  history.checked.add(lessonId);
  try {
    const { data, error } = await getSupabase().rpc('claim_feature_lesson', {
      p_user_id: userId, p_lesson_id: lessonId,
    });
    if (error) throw new Error('Could not reserve learning introduction', { cause: error });
    if (typeof data !== 'boolean') throw new Error('Invalid learning admission response');
    if (data && !history.receipts.has(lessonId)) history.receipts.set(lessonId, 'seen');
    return data;
  } catch (error) {
    history.checked.delete(lessonId);
    throw error;
  }
}

/** Terminal outcomes never need repeat writes or a downgrade from a late dismissal. */
function isFinished(outcome: LearningReceipt['outcome'] | undefined): boolean {
  return outcome === 'completed' || outcome === 'used';
}

/** Coalesce concurrent completion writes and remember successful outcomes in memory. */
export async function finishFeatureLesson(userId: string, lesson: FeatureLessonName, outcome: FeatureLessonOutcome): Promise<void> {
  const history = await historyFor(userId);
  const lessonId = FEATURE_LESSON_IDS[lesson];
  const previous = history.receipts.get(lessonId);
  if (isFinished(previous) || previous === outcome) return;
  const key = `${lessonId}:${outcome}`;
  const pending = history.pendingOutcomes.get(key);
  if (pending) { await pending; return; }
  const request = writeOutcome(userId, lessonId, outcome, history);
  history.pendingOutcomes.set(key, request);
  try { await request; }
  finally { history.pendingOutcomes.delete(key); }
}

/** The admission receipt remains intact even if this optional final outcome fails. */
async function writeOutcome(userId: string, lessonId: string, outcome: FeatureLessonOutcome, history: LearningHistory): Promise<void> {
  const { error } = await getSupabase().rpc('finish_feature_lesson', {
    p_user_id: userId, p_lesson_id: lessonId, p_outcome: outcome,
  });
  if (error) throw new Error('Could not save learning outcome', { cause: error });
  if (!isFinished(history.receipts.get(lessonId))) history.receipts.set(lessonId, outcome);
}
